import { useKohdeSynkka } from "./Kohdekonteksti";
import { useLataus, LatausTila } from "./Lataus";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type Kayttaja, type Kohde, type Ainestodistus } from "./api";
import { TILAVARI } from "./Tila";
import { Ohje } from "./Ohje";
import { t, esim, virheTeksti } from "./kieli";

/* Ainestodistukset (EN 10204 3.1) → sulatus. Jäljitettävyys hitsi→osa→sulatus→
   todistus + kattavuus (mitkä hitseissä käytetyt sulatukset on katettu).
   Lataus + sulatuksen osoitus (OCR-ehdotus avuksi, ihminen vahvistaa).

   ⚠ 16.8.–13.9.2026 tämä kommentti lupasi vahvistuksen, jota koodi ei tehnyt: OCR:n ehdotukset yhdistettiin
   käsin syötettyihin ja tallennettiin samassa painalluksessa. Nyt (T288) käsin syötetyt tallentuvat heti,
   ja OCR:n ehdotukset näytetään valitsemattomina — vain napautettu tallentuu. Kompastus sampo-muistissa
   «Kommentti lupasi ihminen vahvistaa, koodi tallensi OCR:n ehdotukset suoraan». */

const tiedostoBase64 = (f: File) => new Promise<string>((res, rej) => {
  const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error("luku")); r.readAsDataURL(f);
});

export default function Ainestodistukset({ kayttaja }: { kayttaja: Kayttaja }) {
  /*
   * Näkymässä ei ollut roolitarkistusta lainkaan — käyttäjä nimettiin
   * `_kayttaja`:ksi eli tietoisesti käyttämättömäksi. Kaikki reitit
   * (`/api/ainestodistukset*`) vaativat kuitenkin `AINE_ROOLIT`in, joten
   * esimerkiksi lukija näki latauslomakkeen ja poistonapit jotka API
   * hylkää. Puuttuva portti, ei erimielisyys.
   *
   * Lista on sama kuin `AINE_ROOLIT` (routes.ts).
   */
  const saaKasitella = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r))
    // T488: oikeus `materiaalit` (koko yritys tai kohde) — palvelin tarkistaa kohteen.
    || (kayttaja.oikeudet.includes("materiaalit") || (kayttaja.kohdeOikeudet?.["materiaalit"] ?? []).length > 0);

  /*
   * ⛔ Portti on OMASSA komponentissaan, ei aikaisena `return`ina.
   *
   * Tässä oli 31.8.2026 asti `if (!saaKasitella) return …` ja KAIKKI
   * kymmenen hookia sen alapuolella. Se on sama vika joka kaatoi
   * kolme instanssia 41 tunniksi (`main.tsx`, React #310): hook
   * aikaisen returnin jälkeen ajaa vain toisella haaralla, ja React
   * kaatuu sinä hetkenä kun haara vaihtuu.
   *
   * Se ei ollut kaatunut vain siksi ettei kenenkään oikeudet muutu
   * kesken istunnon. Latentti, ei turvallinen — täsmälleen kuten
   * `main.tsx` ennen kuin kirjautumistila muuttui.
   *
   * Jako on turvallisempi kuin hookien siirtäminen returnin
   * yläpuolelle: silloin efektit ampuisivat API-kutsuja käyttäjälle
   * jolta reitit joka tapauksessa evätään. Nyt sisältö ei mounttaa.
   *
   * Löytyi `react-hooks/rules-of-hooks`-säännöllä minuutteja sen
   * asentamisen jälkeen.
   */
  /* 178 (Toni 26.9.2026): lukuoikeus `ainestodistukset_luku` roolilistan rinnalle — koko yritys tai kohde.
     Lukija näkee listan ja kattavuuden, ei lisäystä eikä poistoa. Sama ehto kuin API:n ainestodistustenKohteet. */
  // 10.10.2026 (Toni): tilaaja (laji ulkopuolinen) lukee oman kohteensa todistukset.
  const saaLukea = saaKasitella || kayttaja.oikeudet.includes("ainestodistukset_luku")
    || (kayttaja.laji === "ulkopuolinen" && !kayttaja.roolit.includes("tarkastaja"))
    || (kayttaja.kohdeOikeudet?.["ainestodistukset_luku"] ?? []).length > 0;
  if (!saaLukea) {
    return (
      <section className="panel">
        {/* h1 myös oikeushaarassa: näkymän NIMI ei riipu siitä saako
            sitä käyttää, ja portti tunnistaa näkymän h1:stä. */}
        <div className="panel__otsikko"><h1>{t("valikko.ainestodistukset")}</h1></div>
        <p className="dim">
          {t("aines.ei_oikeutta")}
        </p>
      </section>
    );
  }
  // T464 (Toni 10.10.2026): tilaajalle neutraali «Sulatusnumero puuttuu», ei urakoitsijan «velka».
  const asiakas = kayttaja.laji === "ulkopuolinen" && !kayttaja.roolit.includes("tarkastaja");
  return <AinestodistuksetSisalto muokattava={saaKasitella} asiakas={asiakas} />;
}

function AinestodistuksetSisalto({ muokattava, asiakas }: { muokattava: boolean; asiakas: boolean }) {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohde, setKohde] = useState<number | "">("");
  const { ylapalkissa } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: true });   // T426
  const [lista, setLista] = useState<Ainestodistus[]>([]);
  const [kattavuus, setKattavuus] = useState<{ sulatus: string; todistus: boolean }[]>([]);
  const [haku, setHaku] = useState("");
  const [uusiAuki, setUusiAuki] = useState(false);
  const [latausvirhe, setLatausvirhe] = useState<string | null>(null);

  const klKohteet = useLataus();
  useEffect(() => { klKohteet.seuraa(api.kohteet().then(setKohteet)); }, [klKohteet.kerta]);
  // Virhe näkyy eikä muutu tyhjäksi listaksi (26.9.2026): lukuoikeuden haltija saa 404 kohteesta jota ei ole annettu,
  // ja tyhjä lista lukisi «todistuksia 0» — väite, ei mittaus.
  const lataa = useCallback(() => {
    const k = kohde === "" ? undefined : kohde;
    setLatausvirhe(null);
    const virheena = (e: unknown) => setLatausvirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("aines.lataus_virhe"));
    api.ainestodistukset(k).then(setLista).catch((e) => { setLista([]); virheena(e); });
    api.ainestodistusKattavuus(k).then(setKattavuus).catch((e) => { setKattavuus([]); virheena(e); });
  }, [kohde]);
  useEffect(() => { lataa(); }, [lataa]);

  const katettu = kattavuus.filter((k) => k.todistus).length;
  const puuttuu = kattavuus.filter((k) => !k.todistus);
  const naytettavat = useMemo(() => lista.filter((a) => {
    if (!haku.trim()) return true;
    const h = haku.toLowerCase();
    return (a.materiaali ?? "").toLowerCase().includes(h) || (a.huomiot ?? "").toLowerCase().includes(h)
      || a.sulatukset.some((s) => s.toLowerCase().includes(h));
  }), [lista, haku]);

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.ainestodistukset")}</h1>
          {muokattava && <button className="nappi nappi--paa" onClick={() => setUusiAuki((v) => !v)}>{uusiAuki ? t("hl.sulje") : t("aines.uusi")}</button>}
        </div>
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />

      {/* Ohje ylös (Toni 24.9.2026). «Kuka» = AINE_ROOLIT (routes.ts) + moduuli hitsausloki; PDF vaatii liitteet. */}
      <Ohje
        kuka={<>{t("aines.ohje.kuka")}</>}
        luvut={<>{t("aines.ohje.luvut")}</>} />

      <div style={{ margin: "0 0 14px" }}>
        {!ylapalkissa && <label className="dim" style={{ fontSize: 13 }}>{t("kuittaus.kohde")}{" "}
          <select value={kohde} onChange={(e) => setKohde(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("aines.kaikki_kohteet")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select>
        </label>}
      </div>
      {latausvirhe && <p className="huom huom--vika" role="alert">{latausvirhe}</p>}

      {muokattava && uusiAuki && <UusiTodistus kohteet={kohteet} oletusKohde={kohde} kattamattomat={puuttuu.map((p) => p.sulatus)}
        onValmis={() => { setUusiAuki(false); lataa(); }} />}

      {/* Kattavuus */}
      {kattavuus.length > 0 && (
        <section className="panel" style={{ marginBottom: 18 }}>
          <div className="panel__head"><h2>{t("aines.kattavuus")}</h2>
            <span className="hint">{t("aines.kattavuus_hint")}</span></div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 24px", padding: "10px 0", fontSize: 14 }}>{/* 5.10.2026: ilman rivitystä rivi pakotti rungon 423 px:iin 375 px:n näkymässä */}
            <span><b style={{ fontSize: 22, color: TILAVARI.kunnossa }}>{katettu}</b> {t("aines.katettu")}</span>
            <span><b style={{ fontSize: 22, color: "var(--puna)" }}>{puuttuu.length}</b> {t("aines.ilman")}</span>
            <span className="dim" style={{ alignSelf: "center" }}>{t("aines.prosentti", { p: Math.round((katettu / (kattavuus.length || 1)) * 100) })}</span>
          </div>
          {puuttuu.length > 0 && (
            <details>
              <summary style={{ cursor: "pointer", fontSize: 13 }}>{t(asiakas ? "aines.nayta_puuttuvat_tilaaja" : "aines.nayta_puuttuvat", { n: puuttuu.length })}</summary>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, maxHeight: 180, overflow: "auto" }}>
                {puuttuu.slice(0, 400).map((p) => <span key={p.sulatus} className="mono"
                  style={{ fontSize: 12, padding: "2px 7px", border: "1px solid var(--viiva)" }}>{p.sulatus}</span>)}
              </div>
            </details>
          )}
        </section>
      )}

      {/* Todistuslista */}
      <section className="panel scroll">
        <div className="panel__head"><h2>{t("aines.todistukset")}</h2><span className="hint">{t("aines.kpl", { n: naytettavat.length })}</span></div>
        <input value={haku} onChange={(e) => setHaku(e.target.value)} placeholder={t("aines.haku")} style={{ margin: "0 0 10px", minWidth: 240 }} />
        {naytettavat.length === 0 && <p className="dim">{t("aines.ei_todistuksia")}</p>}
        {naytettavat.length > 0 && (
          <div className="taulu-ikkuna" data-taulu-ikkuna="ainestodistukset">
          {/* Vieritys (Toni 5.10.2026 klo 11.40): säiliö näkymän korkuinen, otsikko sticky, vaakavieritys aina ruudulla. */}
          <table className="tbl" style={{ fontSize: 13 }}>
            <thead><tr><th>{t("aines.materiaali")}</th><th>{t("hl.sulatukset")}</th><th>{t("aines.kuvaus")}</th><th>PDF</th><th></th></tr></thead>
            <tbody>
              {naytettavat.slice(0, 500).map((a) => (
                <tr key={a.id}>
                  <td className="mono">{a.materiaali ?? ""}</td>
                  {/* TODISTUS ILMAN SULATUSTA EI KATA MITÄÄN, ja se
                      näkyi tyhjänä soluna. Mitattu 2.9.2026 demossa:
                      yksi todistus luotu 31.8. lomakkeelta, PDF
                      liitteenä, 0 sulatusta, ei materiaalia — ollut
                      tyhjänä kaksi vuorokautta.

                      `POST /api/ainestodistukset` ei vaadi mitään
                      (ei `required`-listaa), ja sulatukset liitetään
                      erikseen jälkikäteen. Jokainen todistus syntyy
                      siis nollalla sulatuksella, eikä mikään pakota
                      sitä saamaan yhtään.

                      Validointi luontihetkellä rikkoisi kulun
                      luo → lataa PDF → OCR → liitä sulatukset.
                      Merkintä ei riko: tyhjä solu on tiedon puute,
                      merkitty tyhjä on tehtävä. */}
                  <td data-sulatukset={a.sulatukset.length}>
                    {/* max-width ei pure taulukon soluun (auto layout) — katto on sisäisessä lohkossa. Mitattu 5.10.: solu 313 px, katto 28ch ≈ 210 px. */}
                    <div className="sulatukset-solu">
                    {a.sulatukset.length === 0
                      ? <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                                       color: "var(--varoitus)", border: "2px solid var(--varoitus)",
                                       padding: "1px 6px", whiteSpace: "nowrap" }}
                              title={t("aines.ei_sulatuksia_title")}>
                          {t("aines.ei_sulatuksia")}
                        </span>
                      : <Sulatuslista sulatukset={a.sulatukset} />}
                    </div>
                  </td>
                  <td className="dim">{a.huomiot ?? ""}</td>
                  <td>{a.liite_id ? <a href={api.liiteOsoite(a.liite_id)} target="_blank" rel="noreferrer">{t("aines.avaa")} ↗</a> : <span className="dim">–</span>}</td>
                  <td>{muokattava && <button className="nappi nappi--kevyt" onClick={() => { if (confirm(t("aines.poista_kysymys"))) api.poistaAinestodistus(a.id).then(lataa); }}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </>
  );
}

/* ── uusi todistus: lataa PDF → OCR-ehdotus → vahvista sulatukset ── */
function UusiTodistus({ kohteet, oletusKohde, kattamattomat, onValmis }:
  { kohteet: Kohde[]; oletusKohde: number | ""; kattamattomat: string[]; onValmis: () => void }) {
  const [kohdeId, setKohdeId] = useState<number | "">(oletusKohde);
  const [tiedosto, setTiedosto] = useState<File | null>(null);
  const [materiaali, setMateriaali] = useState("");
  const [toimittaja, setToimittaja] = useState("");
  const [sulatukset, setSulatukset] = useState("");
  const [vaihe, setVaihe] = useState<"syotto" | "kasittely" | "vahvistus">("syotto");
  const [virhe, setVirhe] = useState<string | null>(null);
  // Vahvistusvaihe: todistus ja käsin syötetyt on jo tallennettu; OCR:n ehdotukset odottavat ihmisen valintaa.
  const [luotuId, setLuotuId] = useState<number | null>(null);
  const [kasinTallennettu, setKasinTallennettu] = useState<string[]>([]);
  const [ehdotukset, setEhdotukset] = useState<string[]>([]);
  const [valitutEhd, setValitutEhd] = useState<string[]>([]);

  async function tallenna() {
    if (!tiedosto) { setVirhe(t("aines.valitse_pdf")); return; }
    setVaihe("kasittely"); setVirhe(null);
    try {
      const sisalto = await tiedostoBase64(tiedosto);
      // Todistus ja PDF yhdellä kutsulla: kaatunut liite ei jätä tyhjää todistusta (13.9.2026).
      const { id } = await api.luoAinestodistus({
        ...(kohdeId !== "" && { kohde_id: kohdeId }),
        ...(materiaali.trim() && { materiaali: materiaali.trim() }),
        ...(toimittaja.trim() && { toimittaja: toimittaja.trim() }),
        liite: { nimi: tiedosto.name, sisalto },
      });
      // Käsin syötetyt ovat käyttäjän päätöksiä: tallennetaan heti.
      const kasin = [...new Set(sulatukset.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))];
      if (kasin.length) await api.asetaAinestodistusSulatukset(id, kasin);
      // OCR-ehdotus (auttaa, ei pakollinen) — EI tallennu ilman valintaa.
      let ocr: string[] = [];
      try { ocr = (await api.ocrAinestodistus(id)).ehdotukset; } catch { /* OCR ei pakollinen */ }
      const uudet = [...new Set(ocr)].filter((s) => !kasin.includes(s));
      if (uudet.length === 0) { onValmis(); return; }
      setLuotuId(id); setKasinTallennettu(kasin); setEhdotukset(uudet); setValitutEhd([]);
      setVaihe("vahvistus");
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : t("ltar.tallennus_virhe")); setVaihe("syotto"); }
  }

  async function vahvistaEhdotukset() {
    if (luotuId === null) return;
    if (valitutEhd.length === 0) { onValmis(); return; }
    setVirhe(null);
    try {
      await api.asetaAinestodistusSulatukset(luotuId, [...kasinTallennettu, ...valitutEhd]);
      onValmis();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : t("ltar.tallennus_virhe")); }
  }

  if (vaihe === "vahvistus") {
    return (
      <div style={{ background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 14, marginBottom: 16 }}
           data-ocr-vahvistus={ehdotukset.length}>
        <p style={{ marginTop: 0 }}>
          <b>{t("aines.ocr_ehdottaa", { n: ehdotukset.length })}</b> {kasinTallennettu.length ? t("aines.tallennettu_kasin") : t("aines.tallennettu")}
          {" "}{t("aines.napauta")}
        </p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {ehdotukset.map((s) => {
            const on = valitutEhd.includes(s);
            return (
              <button key={s} type="button" aria-pressed={on}
                      className={"nappi " + (on ? "nappi--paa" : "nappi--kevyt")}
                      onClick={() => setValitutEhd((v) => (on ? v.filter((x) => x !== s) : [...v, s]))}>
                {on ? "✓ " : "+ "}<span className="mono">{s}</span>
              </button>
            );
          })}
        </div>
        <p style={{ marginBottom: 0 }}>
          <button className="nappi nappi--paa" onClick={() => void vahvistaEhdotukset()}>
            {valitutEhd.length ? t("aines.tallenna_valitut", { n: valitutEhd.length }) : t("aines.valmis_ilman")}
          </button>
        </p>
        {virhe && <p className="huom huom--vika" role="alert" style={{ marginTop: 6 }}>{virhe}</p>}
      </div>
    );
  }

  return (
    <div style={{ background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 14, marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label>{t("aines.pdf")}
          <input type="file" accept="application/pdf" onChange={(e) => setTiedosto(e.target.files?.[0] ?? null)} /></label>
        <label>{t("kuittaus.kohde")}
          <select value={kohdeId} onChange={(e) => setKohdeId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">—</option>{kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}</select></label>
        <label>{t("aines.materiaali")}
          <input value={materiaali} onChange={(e) => setMateriaali(e.target.value)} placeholder={esim("1.4404")} style={{ width: 110 }} /></label>
        <label>{t("aines.toimittaja")}
          <input value={toimittaja} onChange={(e) => setToimittaja(e.target.value)} /></label>
        <label style={{ flex: 1, minWidth: 180 }}>{t("aines.sulatukset_kentta")}
          <input value={sulatukset} onChange={(e) => setSulatukset(e.target.value)} placeholder={esim("AB12345 CD67890 …")} /></label>
        <button className="nappi nappi--paa" disabled={vaihe === "kasittely" || !tiedosto} onClick={tallenna}>
          {vaihe === "kasittely" ? t("aines.ocr_tallennus") : t("ltar.tallenna")}</button>
      </div>
      <p className="dim" style={{ marginTop: 8 }}>
        {t("aines.lomake_ohje")} {t("aines.kattamattomia", { n: kattamattomat.length })}
      </p>
      {virhe && <p className="huom huom--vika" role="alert" style={{ marginTop: 6 }}>{virhe}</p>}
    </div>
  );
}

/* Sulatuslista (5.10.2026): 5 ensimmäistä näkyvissä, loput «+ N muuta» -avauksen takana — pitkä lista ei levitä saraketta
   (katto 28ch CSS:ssä) eikä piilota Kuvaus-saraketta. Haku lukee yhä kaikki sulatukset (naytettavat-suodatin), ei vain näkyvät. */
function Sulatuslista({ sulatukset }: { sulatukset: string[] }) {
  const NAKYVIA = 5;
  const [auki, setAuki] = useState(false);
  const nakyvat = auki ? sulatukset : sulatukset.slice(0, NAKYVIA);
  const loput = sulatukset.length - NAKYVIA;
  return (
    <>
      {nakyvat.map((s) => <span key={s} className="mono" style={{ fontSize: 12, marginRight: 6 }}>{s}</span>)}
      {loput > 0 && (
        <button type="button" className="nappi nappi--kevyt" style={{ fontSize: 12, padding: "0 6px" }} data-sulatukset-lisaa={loput}
                onClick={() => setAuki((v) => !v)} aria-expanded={auki}>
          {auki ? t("aines.vahemman") : t("aines.n_muuta", { n: loput })}
        </button>
      )}
    </>
  );
}
